import React, { useState } from 'react';
import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TimePicker } from "@/components/admin/locations/components/time-picker";
import { UseFormReturn } from 'react-hook-form';
import { AgeGroupVariationFormData } from '../schemas/ageGroupVariationSchema';
import { RequiredFieldLabel } from "@/components/ui/required-field-label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface TimingFieldsProps {
  form: UseFormReturn<AgeGroupVariationFormData>;
}

const OvernightDaysFields: React.FC<{ form: UseFormReturn<AgeGroupVariationFormData> }> = ({ form }) => {
  const initialDrop = form.getValues("timings.dropOffDay");
  const initialPick = form.getValues("timings.pickUpDay");
  const hasInitial = (initialDrop && initialDrop !== "") || (initialPick && initialPick !== "");
  const [open, setOpen] = useState<boolean>(!!hasInitial);

  const NOT_SPECIFIED = "__NOT_SPECIFIED__";
  const dayOptions = [
    { label: "Not specified", value: NOT_SPECIFIED },
    { label: "Monday", value: "M" },
    { label: "Tuesday", value: "T" },
    { label: "Wednesday", value: "W" },
    { label: "Thursday", value: "R" },
    { label: "Friday", value: "F" },
    { label: "Saturday", value: "S" },
    { label: "Sunday", value: "U" }
  ];

  return (
    <div className="px-1 mt-2">
      <button
        type="button"
        className="text-xs underline"
        onClick={() => setOpen(v => !v)}
      >
        Enter drop-off, pick-up days (for overnight camps)
      </button>

      {open && (
        <div className="mt-3 grid grid-cols-[1fr_1fr] gap-3">
          {/* Drop-off day */}
          <FormField
            control={form.control}
            name="timings.dropOffDay"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Drop-off day</FormLabel>
                <FormControl>
                  <Select
                    value={(field.value ?? "") === "" ? NOT_SPECIFIED : field.value}
                    onValueChange={(v) => field.onChange(v === NOT_SPECIFIED ? "" : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Not specified" />
                    </SelectTrigger>
                    <SelectContent>
                      {dayOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage tabIndex={-1} />
              </FormItem>
            )}
          />

          {/* Pick-up day */}
          <FormField
            control={form.control}
            name="timings.pickUpDay"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Pick-up day</FormLabel>
                <FormControl>
                  <Select
                    value={(field.value ?? "") === "" ? NOT_SPECIFIED : field.value}
                    onValueChange={(v) => field.onChange(v === NOT_SPECIFIED ? "" : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Not specified" />
                    </SelectTrigger>
                    <SelectContent>
                      {dayOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage tabIndex={-1} />
              </FormItem>
            )}
          />
        </div>
      )}
    </div>
  );
};

export const TimingFields: React.FC<TimingFieldsProps> = ({
  form
}) => {
  return (
    <div>
      <h3 className="text-lg font-semibold mb-2 text-left px-1">Timings</h3>
      <p className="text-sm text-muted-foreground mb-4 text-left px-1">
        Enter timings for the typical day of this offering. Using the footnote to explain unusual days.
      </p>

      <div className="mb-6 px-1">
        <div className="grid grid-cols-[1fr_1fr_1fr] gap-2 mb-2">
          <div></div>
          <div className="text-left text-sm font-medium">Start time</div>
          <div className="text-left text-sm font-medium">End time</div>
        </div>

        {/* Before care */}
        <div className="grid grid-cols-[1fr_1fr_1fr] gap-2 mb-3 items-center">
          <Label className="text-left">Before care (if available)</Label>
          <FormField
            control={form.control}
            name="timings.beforeCareStart"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <TimePicker
                    value={typeof field.value === 'string' ? null : field.value}
                    onChange={field.onChange}
                    onBlur={() => {
                      form.trigger(['timings.beforeCareStart', 'timings.beforeCareEnd']);
                    }}
                    error={!!form.formState.errors.timings?.beforeCareStart}
                  />
                </FormControl>
                <FormMessage tabIndex={-1} />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="timings.beforeCareEnd"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <TimePicker
                    value={typeof field.value === 'string' ? null : field.value}
                    onChange={field.onChange}
                    onBlur={() => {
                      form.trigger(['timings.beforeCareEnd', 'timings.regularDropOffStart']);
                    }}
                    error={!!form.formState.errors.timings?.beforeCareEnd}
                  />
                </FormControl>
                <FormMessage tabIndex={-1} />
              </FormItem>
            )}
          />
        </div>

        {/* Regular drop off */}
        <div className="grid grid-cols-[1fr_1fr_1fr] gap-2 mb-3 items-center">
          <Label className="text-left flex items-center gap-1">
            <span>Regular drop off</span>
            <span className="text-red-500">*</span>
          </Label>
          <FormField
            control={form.control}
            name="timings.regularDropOffStart"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <TimePicker
                    value={typeof field.value === 'string' ? null : field.value}
                    onChange={field.onChange}
                    onBlur={() => {
                      form.trigger(['timings.beforeCareEnd', 'timings.regularDropOffStart', 'timings.regularDropOffEnd']);
                    }}
                    error={!!form.formState.errors.timings?.regularDropOffStart}
                  />
                </FormControl>
                <FormMessage tabIndex={-1} />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="timings.regularDropOffEnd"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <TimePicker
                    value={typeof field.value === 'string' ? null : field.value}
                    onChange={field.onChange}
                    onBlur={() => {
                      form.trigger(['timings.regularDropOffStart', 'timings.regularDropOffEnd', 'timings.regularProgrammingStart']);
                    }}
                    error={!!form.formState.errors.timings?.regularDropOffEnd}
                  />
                </FormControl>
                <FormMessage tabIndex={-1} />
              </FormItem>
            )}
          />
        </div>

        {/* Regular programming */}
        <div className="grid grid-cols-[1fr_1fr_1fr] gap-2 mb-3 items-center">
          <Label className="text-left flex items-center gap-1">
            <span>Regular programming</span>
            <span className="text-red-500">*</span>
          </Label>
          <FormField
            control={form.control}
            name="timings.regularProgrammingStart"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <TimePicker
                    value={typeof field.value === 'string' ? null : field.value}
                    onChange={field.onChange}
                    onBlur={() => {
                      form.trigger(['timings.regularDropOffEnd', 'timings.regularProgrammingStart', 'timings.regularProgrammingEnd']);
                    }}
                    error={!!form.formState.errors.timings?.regularProgrammingStart}
                  />
                </FormControl>
                <FormMessage tabIndex={-1} />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="timings.regularProgrammingEnd"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <TimePicker
                    value={typeof field.value === 'string' ? null : field.value}
                    onChange={field.onChange}
                    onBlur={() => {
                      form.trigger(['timings.regularProgrammingStart', 'timings.regularProgrammingEnd', 'timings.regularPickUpStart']);
                    }}
                    error={!!form.formState.errors.timings?.regularProgrammingEnd}
                  />
                </FormControl>
                <FormMessage tabIndex={-1} />
              </FormItem>
            )}
          />
        </div>

        {/* Regular pick up */}
        <div className="grid grid-cols-[1fr_1fr_1fr] gap-2 mb-3 items-center">
          <Label className="text-left flex items-center gap-1">
            <span>Regular pick up</span>
            <span className="text-red-500">*</span>
          </Label>
          <FormField
            control={form.control}
            name="timings.regularPickUpStart"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <TimePicker
                    value={typeof field.value === 'string' ? null : field.value}
                    onChange={field.onChange}
                    onBlur={() => {
                      form.trigger(['timings.regularProgrammingEnd', 'timings.regularPickUpStart', 'timings.regularPickUpEnd']);
                    }}
                    error={!!form.formState.errors.timings?.regularPickUpStart}
                  />
                </FormControl>
                <FormMessage tabIndex={-1} />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="timings.regularPickUpEnd"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <TimePicker
                    value={typeof field.value === 'string' ? null : field.value}
                    onChange={field.onChange}
                    onBlur={() => {
                      form.trigger(['timings.regularPickUpStart', 'timings.regularPickUpEnd', 'timings.afterCareStart']);
                    }}
                    error={!!form.formState.errors.timings?.regularPickUpEnd}
                  />
                </FormControl>
                <FormMessage tabIndex={-1} />
              </FormItem>
            )}
          />
        </div>

        {/* After care */}
        <div className="grid grid-cols-[1fr_1fr_1fr] gap-2 mb-3 items-center">
          <Label className="text-left">After care (if available)</Label>
          <FormField
            control={form.control}
            name="timings.afterCareStart"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <TimePicker
                    value={typeof field.value === 'string' ? null : field.value}
                    onChange={field.onChange}
                    onBlur={() => {
                      form.trigger(['timings.regularPickUpEnd', 'timings.afterCareStart', 'timings.afterCareEnd']);
                    }}
                    error={!!form.formState.errors.timings?.afterCareStart}
                    defaultPeriod="PM"
                  />
                </FormControl>
                <FormMessage tabIndex={-1} />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="timings.afterCareEnd"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <TimePicker
                    value={typeof field.value === 'string' ? null : field.value}
                    onChange={field.onChange}
                    onBlur={() => {
                      form.trigger(['timings.afterCareStart', 'timings.afterCareEnd']);
                    }}
                    error={!!form.formState.errors.timings?.afterCareEnd}
                    defaultPeriod="PM"
                  />
                </FormControl>
                <FormMessage tabIndex={-1} />
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* Timing footnote */}
      <FormField
        control={form.control}
        name="timings.timingNote"
        render={({ field }) => (
          <FormItem className="px-1">
            <FormLabel className="text-left block">Timing footnote (optional)</FormLabel>
            <FormControl>
              <Input 
                {...field} 
                value={field.value || ""}
                placeholder=""
              />
            </FormControl>
            <FormDescription className="text-xs">
              eg. "Fridays end at 3:00 PM"
            </FormDescription>
            <FormMessage tabIndex={-1} />
          </FormItem>
        )}
      />

      <OvernightDaysFields form={form} />
    </div>
  );
};
